import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, CheckCircle2 } from 'lucide-react';

// Curated, plausible "live activity" events — gives the page a quiet pulse without
// implying any specific client data. Pure marketing animation.
const EVENTS = [
  { type: 'recon',     city: 'Mumbai',    text: 'Reconciliation cycle completed', value: 'POS · Books · Bank matched', accent: '#A8D5BA' },
  { type: 'supplier',  city: 'Pune',      text: 'Supplier overcharge detected',   value: 'Rate-card variance flagged', accent: '#E8D29A' },
  { type: 'settle',    city: 'Bangalore', text: 'Settlement variance flagged',    value: 'Aggregator hold-back identified', accent: '#C9A66B' },
  { type: 'dispute',   city: 'Goa',       text: 'Dispute drafted',                value: 'Owner-signed packet prepared', accent: '#6FB592' },
  { type: 'menu',      city: 'Hyderabad', text: 'Menu mix analysis refreshed',    value: 'Margin contribution updated', accent: '#A8D5BA' },
  { type: 'recon',     city: 'Chennai',   text: 'Three-way recon completed',      value: 'Zero unmatched transactions', accent: '#6FB592' },
  { type: 'mandi',     city: 'Delhi NCR', text: 'Mandi price benchmarking',       value: 'New negotiation opportunity', accent: '#E8D29A' },
  { type: 'weekly',    city: 'Kolkata',   text: 'Weekly Brief delivered',         value: 'Composite PDF dispatched', accent: '#C9A66B' },
];

export const LiveTicker = () => {
  const [idx, setIdx] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % EVENTS.length);
    }, 3400);
    return () => clearInterval(intervalRef.current);
  }, []);

  const e = EVENTS[idx];

  return (
    <section className="relative py-8 md:py-10 border-y border-white/[0.06] bg-black/30 backdrop-blur-xl overflow-hidden" data-testid="live-ticker">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="flex items-center gap-4 md:gap-6">
          {/* Live pulse dot */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="relative">
              <div className="w-2 h-2 rounded-full bg-[#6FB592]" />
              <div className="absolute inset-0 w-2 h-2 rounded-full bg-[#6FB592] animate-ping opacity-75" />
            </div>
            <Activity className="w-3.5 h-3.5 text-white/45" />
            <span className="text-[10px] md:text-[11px] uppercase tracking-[0.28em] text-white/55 font-semibold whitespace-nowrap">
              Live · Practice activity
            </span>
          </div>

          <span className="hidden md:inline-block w-1 h-1 rounded-full bg-white/20 flex-shrink-0" />

          {/* Morphing event */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${idx}-${e.text}`}
                initial={{ opacity: 0, y: 8, filter: 'blur(4px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -6, filter: 'blur(4px)' }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2.5 md:gap-4 min-w-0"
                data-testid={`ticker-event-${idx}`}
              >
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" style={{ color: e.accent }} />
                <span className="text-sm md:text-[15px] text-white font-medium truncate">{e.text}</span>
                <span className="hidden md:inline-block w-1 h-1 rounded-full bg-white/20 flex-shrink-0" />
                <span className="hidden md:inline text-sm text-white/55 truncate">{e.value}</span>
                <span className="hidden lg:inline-block w-1 h-1 rounded-full bg-white/20 flex-shrink-0" />
                <span className="hidden lg:inline text-[11px] uppercase tracking-[0.22em] text-white/40 truncate">{e.city}</span>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiveTicker;
